import { createSlice } from "@reduxjs/toolkit";

const athleteSlice = createSlice({
  name: "athletes",
  initialState: {
    profilesById: {},
    athletes: [],
    favouriteAthletes: [],
    similarAthletes: [],
    profile: null,
    offers: [],
    accolades: [],
    loading: false,
    error: null,
    config: {
      showOffers: true,
      showTrackittScore: true,
      showPositionScore: true,
      searchEvent: false,
      eventId: undefined,
      showProfile: true,
      showRank: true,
      showLikes: true,
    },
    buttons: {
      combine: {
        tab: true,
        athleteTabSelected: true,
        positionTabSelected: false,
      },
      measurements: {
        tab: true,
        athleteTabSelected: true,
        positionTabSelected: false,
      },
    },
    positionDrill: [],
    reload: false,
    likedOverrides: {},
    displayName: null,
  },
  reducers: {
    fetchAthleteProfileRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAthleteProfileSuccess: (state, action) => {
      const profile = action.payload;
      state.loading = false;
      state.profile = profile;
      state.profilesById[profile.id] = profile;
    },
    fetchAthleteProfileFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchOffersSuccess: (state, action) => {
      state.offers = action.payload;
    },
    fetchAccoladesSuccess: (state, action) => {
      state.accolades = action.payload;
    },
    fetchProfilePhotoRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchProfilePhotoSuccess: (state, action) => {
      state.loading = false;
      state.profilePhoto = action.payload;
    },
    fetchProfilePhotoFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    removeAccolade: (state, action) => {
      const result = state.accolades.filter(
        (item) => item.id !== action.payload,
      );
      state.accolades = result;
    },
    removeOffer: (state, action) => {
      const result = state.offers.filter((item) => item.id !== action.payload);
      state.offers = result;
    },
    setProfile: (state, action) => {
      state.profile = action.payload;
    },
    setDisplayName: (state, action) => {
      const { firstName, lastName } = action.payload ?? {};
      state.displayName = {
        firstName: firstName ?? "",
        lastName: lastName ?? "",
      };
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setScoreConfig: (state, action) => {
      state.config.showOffers = action.payload.showOffers;
      state.config.showTrackittScore = action.payload.showTrackittScore;
      state.config.showPositionScore = action.payload.showPositionScore;
      state.config.searchEvent = action.payload.searchEvent;
      state.config.showProfile = action.payload.showProfile;
      state.config.showRank = action.payload.showRank;
      state.config.showLikes = action.payload.showLikes;
      state.config.eventId = action.payload.eventId;
    },
    setEventId: (state, action) => {
      state.config.eventId = action.payload;
    },
    setButtonsCombine: (state, action) => {
      state.buttons.combine.athleteTabSelected = action.payload.athlete;
      state.buttons.combine.positionTabSelected = action.payload.position;
      state.buttons.measurements.athleteTabSelected = action.payload.athlete;
      state.buttons.measurements.positionTabSelected = action.payload.position;
    },
    setShowTab: (state, action) => {
      state.buttons.combine.tab = action.payload.combine;
      state.buttons.measurements.tab = action.payload.measurement;
    },
    setPositionDrill: (state, action) => {
      state.positionDrill = action.payload;
    },
    setReload: (state, action) => {
      state.reload = action.payload;
    },
    setAthletes: (state, action) => {
      state.athletes = action.payload;
    },
    setFavouriteAthletes: (state, action) => {
      state.favouriteAthletes = action.payload;
      state.likedOverrides = {};
    },
    setSimilarAthletes: (state, action) => {
      state.similarAthletes = action.payload;
    },
    seedLikedOverride: (state, action) => {
      const { athleteUserId, liked } = action.payload;
      if (!(athleteUserId in state.likedOverrides)) {
        state.likedOverrides[athleteUserId] = liked;
      }
    },
    toggleFavouriteLocal: (state, action) => {
      const { athleteUserId, liked } = action.payload;

      state.likedOverrides[athleteUserId] = liked;

      const fav = state.favouriteAthletes?.find(
        (a) => a.userId === athleteUserId,
      );
      if (fav) fav.liked = liked;

      if (state.profile?.id === athleteUserId) {
        state.profile.likes = Math.max(
          0,
          (state.profile.likes || 0) + (liked ? 1 : -1),
        );
      }
    },

    adjustAthleteLikes: (state, action) => {
      const { athleteUserId, delta, total } = action.payload;
      const hasValidTotal = typeof total === "number" && !Number.isNaN(total);
      const safeDelta =
        typeof delta === "number" && !Number.isNaN(delta) ? delta : 0;
      const nextLikes = (current) =>
        hasValidTotal ? total : Math.max(0, (current || 0) + safeDelta);

      if (state.profile?.id === athleteUserId) {
        state.profile.likes = nextLikes(state.profile.likes);
      }

      const fav = state.favouriteAthletes?.find(
        (a) => a.userId === athleteUserId,
      );
      if (fav) fav.likes = nextLikes(fav.likes);
    },
  },
});

export const {
  fetchAthleteProfileRequest,
  fetchAthleteProfileSuccess,
  fetchAthleteProfileFail,
  fetchOffersSuccess,
  fetchAccoladesSuccess,
  fetchProfilePhotoRequest,
  fetchProfilePhotoSuccess,
  fetchProfilePhotoFail,
  removeAccolade,
  removeOffer,
  setProfile,
  setDisplayName,
  setLoading,
  setScoreConfig,
  setEventId,
  setButtonsCombine,
  setShowTab,
  setPositionDrill,
  setReload,
  setAthletes,
  setFavouriteAthletes,
  setSimilarAthletes,
  toggleFavouriteLocal,
  seedLikedOverride,
  adjustAthleteLikes,
} = athleteSlice.actions;

export default athleteSlice.reducer;
